import { z } from 'zod'

import { decimalSchema } from './prisma-zod'
import { BillingAuthorizationTypeSchema } from './entity-billing-profile'
import { createPaginatedResponseSchema } from './pagination'

export const BillingAuthorizationStatusSchema = z.enum([
  'DRAFT',
  'ACTIVE',
  'EXHAUSTED',
  'EXPIRED',
  'CANCELLED',
  'ARCHIVED',
])

export type BillingAuthorizationStatus = z.infer<
  typeof BillingAuthorizationStatusSchema
>

export const BillingAuthorizationStatusLabels: Record<
  BillingAuthorizationStatus,
  string
> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  EXHAUSTED: 'Exhausted',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
  ARCHIVED: 'Archived',
}

export const BillingAuthorizationSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  entityId: z.string().uuid(),
  billingProfileId: z.string().uuid().nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  contractId: z.string().uuid().nullable().optional(),
  authorizationType: BillingAuthorizationTypeSchema,
  referenceNumber: z.string().nullable().optional(),
  label: z.string(),
  status: BillingAuthorizationStatusSchema,
  authorizedAmount: decimalSchema.nullable().optional(),
  consumedAmount: decimalSchema.default(0),
  consumedTimeAmount: decimalSchema.default(0),
  consumedExpenseAmount: decimalSchema.default(0),
  importedConsumedAmount: decimalSchema.default(0),
  remainingAmount: decimalSchema.nullable().optional(),
  currency: z.string().default('USD'),
  effectiveDate: z.date().nullable().optional(),
  expirationDate: z.date().nullable().optional(),
  requiredOnInvoice: z.boolean().default(false),
  invoiceInstructions: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  documentUrl: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
})

export type BillingAuthorization = z.infer<typeof BillingAuthorizationSchema>

export const BillingAuthorizationsResponseSchema = createPaginatedResponseSchema(
  BillingAuthorizationSchema,
)

export type BillingAuthorizationsResponse = z.infer<
  typeof BillingAuthorizationsResponseSchema
>

export const UpsertBillingAuthorizationSchema = z.object({
  entityId: z.string().uuid(),
  billingProfileId: z.string().uuid().nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  contractId: z.string().uuid().nullable().optional(),
  authorizationType: BillingAuthorizationTypeSchema,
  referenceNumber: z.string().trim().nullable().optional(),
  label: z.string().trim().min(1),
  status: BillingAuthorizationStatusSchema.default('DRAFT'),
  authorizedAmount: decimalSchema.nullable().optional(),
  currency: z.string().trim().min(3).max(3).default('USD'),
  effectiveDate: z.coerce.date().nullable().optional(),
  expirationDate: z.coerce.date().nullable().optional(),
  requiredOnInvoice: z.boolean().default(false),
  invoiceInstructions: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  documentUrl: z
    .string()
    .trim()
    .url('Invalid document URL')
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  metadata: z.record(z.unknown()).default({}),
})

export type UpsertBillingAuthorization = z.infer<
  typeof UpsertBillingAuthorizationSchema
>

export const UpdateBillingAuthorizationSchema =
  UpsertBillingAuthorizationSchema.partial()

export type UpdateBillingAuthorization = z.infer<
  typeof UpdateBillingAuthorizationSchema
>

export const BillingAuthorizationSourceTypeSchema = z.enum(['time', 'expense'])

export type BillingAuthorizationSourceType = z.infer<
  typeof BillingAuthorizationSourceTypeSchema
>

export const BillingAuthorizationSourceRowSchema = z.object({
  id: z.string().uuid(),
  type: BillingAuthorizationSourceTypeSchema,
  date: z.coerce.date(),
  memberName: z.string().nullable(),
  workItem: z.string().nullable(),
  description: z.string().nullable(),
  billableAmount: decimalSchema,
  status: z.string(),
})

export type BillingAuthorizationSourceRow = z.infer<
  typeof BillingAuthorizationSourceRowSchema
>

export const BillingAuthorizationSourcesQuerySchema = z.object({
  type: BillingAuthorizationSourceTypeSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(25),
})

export type BillingAuthorizationSourcesQuery = z.infer<
  typeof BillingAuthorizationSourcesQuerySchema
>

export const BillingAuthorizationSourcesResponseSchema =
  createPaginatedResponseSchema(BillingAuthorizationSourceRowSchema)

export type BillingAuthorizationSourcesResponse = z.infer<
  typeof BillingAuthorizationSourcesResponseSchema
>

export const EligibleBillingAuthorizationQuerySchema = z.object({
  entityId: z.string().uuid(),
  projectId: z.string().uuid().optional().nullable(),
  contractId: z.string().uuid().optional().nullable(),
  invoiceDate: z.coerce.date().optional(),
})

export type EligibleBillingAuthorizationQuery = z.infer<
  typeof EligibleBillingAuthorizationQuerySchema
>
